<template>
  <div class="pie-chart-wrapper" :class="{ 'horizontal-layout': horizontal }">
    <ExportButton
      v-if="showExport"
      style="position: absolute; top: 8px; right: 8px; z-index: 10"
      @click="exportData"
      tooltip="Export to CSV"
    />
    
    <div :class="horizontal ? 'chart-container' : ''">
      <apexchart
        type="donut"
        :options="chartOptions"
        :series="series"
        :height="horizontal ? 250 : 220"
      ></apexchart>
    </div>
    
    <v-list density="compact" :class="horizontal ? 'legend-container mt-0' : 'mt-3'" class="pa-0">
      <v-list-item v-for="(item, index) in data" :key="index" class="px-0 py-1">
        <template v-slot:prepend>
          <div 
            :style="{ 
              backgroundColor: colors[index], 
              width: '16px', 
              height: '16px', 
              borderRadius: '4px', 
              marginRight: '12px',
              flexShrink: 0
            }"
          ></div>
        </template>
        <v-list-item-title class="text-body-2">
          {{ item.label }}
        </v-list-item-title>
        <template v-slot:append>
          <div class="text-end">
            <div class="text-body-2 font-weight-bold">{{ formatNumber(item.value) }}</div>
            <div class="text-caption text-medium-emphasis">{{ item.percentage }}%</div>
          </div>
        </template>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import ExportButton from '@/components/TaxDashboard/ExportButton.vue';
import { exportToCSV } from '@/utils/csvExport';

export default {
  name: 'PieChart',
  components: {
    apexchart: VueApexCharts,
    ExportButton,
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    showExport: {
      type: Boolean,
      default: true
    },
    customColors: {
      type: Array,
      default: null
    }
  },
  computed: {
    series() {
      return this.data.map(item => item.value);
    },
    labels() {
      return this.data.map(item => item.label);
    },
    colors() {
      return this.customColors || ['#1976D2', '#43A047', '#FB8C00', '#E53935', '#8E24AA', '#00ACC1'];
    },
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: this.labels,
        colors: this.colors,
        legend: {
          show: false
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(0) + '%';
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold'
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: false
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString('en-US');
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: '100%'
            }
          }
        }]
      };
    }
  },
  methods: {
    formatNumber(num) {
      return num.toLocaleString('en-US');
    },
    exportData() {
      const headers = [
        { key: 'label', label: 'Category' },
        { key: 'value', label: 'Value' },
        { key: 'percentage', label: 'Percentage (%)' }
      ];
      const filename = this.title ? this.title.replace(/\s+/g, '_') : 'chart_data';
      exportToCSV(this.data, headers, filename);
    }
  }
};
</script>

<style scoped>
.pie-chart-wrapper {
  width: 100%;
  position: relative;
}

.horizontal-layout {
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-container {
  flex-shrink: 0;
  width: 250px;
}

.legend-container {
  flex: 1;
  min-width: 0;
}
</style>
